import { FormProvider, useForm } from "react-hook-form";
import React,{ useState, useRef } from "react";
import {
    Box,
    Button,
    Collapse,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    useTheme,
} from "@mui/material";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { i18n } from "@lingui/core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExpandLess } from "@mui/icons-material";
import StatsFilterForm from "../forms/StatsFilterForm";
import { CollapsiblePanelCard } from "../../shared/components/collapsible-panel-card.component";
import { buildValidatedFilterSearchParams } from "../../utils/filter-date-range.util";

const StatisticsFilterPanel = ({
                                   newLabel,
                                   title,
                                   formModel,
                                   formList,
                                   filterDisabled,
                                   onFilterSubmit,
                                   onFilterReset,
                                   user,
                                   isMobile,
                                   setDataLoaded,
                                   setFilters,
                                   headerExtra,
                                   collapsible = false,
                                   defaultExpanded = true,
                               }) => {
    const methods = useForm();
    const theme = useTheme();
    const { _ } = useLingui();

    // stato per expand/collapse
    const [expanded, setExpanded] = useState(formList.map(() => false));

    // array di ref per ogni form
    const submitRefs = useRef(formList.map(() => React.createRef()));

    const init_filter_values = {};

    const handleExpandClick = (number) => {
        setExpanded((old) => old.map((o, i) => (i === number ? !o : o)));
    };

    const getSearchParams = (data) => {
        const payload = { ...data };
        delete payload.SubSection;

        const { searchParams, invalidFields } = buildValidatedFilterSearchParams(
            payload,
            formList,
            formModel,
        );

        invalidFields.forEach((fieldName) => {
            methods.setValue(fieldName, null, {
                shouldValidate: false,
                shouldDirty: false,
                shouldTouch: false,
            });
        });

        Object.keys(payload).forEach((section) => {
            if (!payload[section] || typeof payload[section] !== 'object' || Array.isArray(payload[section])) {
                return;
            }
            Object.entries(payload[section]).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                if (key.includes("-To") || key.includes("-From")) return;
                const paramKey = `${section}.${key}.equal`;
                if (searchParams[paramKey] == null) {
                    searchParams[paramKey] = value;
                }
            });
        });

        return searchParams;
    };

    const handleFilterSubmit = (data) => {
        const searchParams = getSearchParams(data);
        if (Object.keys(searchParams).length === 0 && buildValidatedFilterSearchParams(
            { ...data, SubSection: undefined },
            formList,
            formModel,
        ).invalidFields.length > 0) {
            return;
        }

        const searchParamsArray = Object.entries(searchParams).map(([key, value]) => ({
            name: key,
            value,
        }));

        if (title !== _(msg`Custom charts`)) {
            if (setDataLoaded) setDataLoaded(false);
            if (setFilters) setFilters(searchParamsArray);
        }
        
        // Chiama onFilterSubmit se è definito, indipendentemente dal title
        if (onFilterSubmit) {
            onFilterSubmit(searchParams);
        }
    };

    const closeAllSections = () => setExpanded(formList.map(() => false));

    const handleClickFilter = () => {
        // Un solo submit con tutti i campi registrati nel FormProvider.
        // requestSubmit su ogni sezione chiamava onFilterSubmit più volte e l'ultima
        // sezione vuota azzerava i filtri attivi (export senza filtri, griglia filtrata).
        methods.handleSubmit(handleFilterSubmit)();
        closeAllSections();
    };

    // Build reset values from all elements in formModel
    const buildResetValues = () => {
        const values = { SubSection: title };
        formList.forEach((formType, formNumber) => {
            if (formModel[formType]?.elements) {
                formModel[formType].elements.forEach((elem) => {
                    if (elem.type === 'datetime' || elem.type === 'date') {
                        // For datetime and date, create From and To fields with null
                        values[`${formList[formNumber]}.${elem.label}-From`] = null;
                        values[`${formList[formNumber]}.${elem.label}-To`] = null;
                    } else if (elem.type === 'select') {
                        // For select, reset to empty string to clear selection
                        values[`${formList[formNumber]}.${elem.label}`] = '';
                    } else {
                        // For other types (text, Latitude, Longitude, etc.), reset to empty string
                        values[`${formList[formNumber]}.${elem.label}`] = '';
                    }
                });
            }
        });
        return values;
    };

    const filterActions = (
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {headerExtra}
            <Button
                disabled={filterDisabled}
                variant="contained"
                sx={{ background: theme.palette.newColors.cyanGradient }}
                size="small"
                onClick={handleClickFilter}
            >
                <Trans>Filter</Trans>
            </Button>
            <Button
                variant="contained"
                size="small"
                sx={{ background: theme.palette.newColors.cyanGradient }}
                onClick={() => {
                    const resetValues = buildResetValues();
                    methods.reset(resetValues, {
                        keepDefaultValues: false,
                        keepValues: false,
                        keepDirty: false,
                        keepErrors: false,
                        keepTouched: false,
                    });
                    formList.forEach((formType, formNumber) => {
                        if (formModel[formType]?.elements) {
                            formModel[formType].elements.forEach((elem) => {
                                if (elem.type === 'select') {
                                    const fieldName = `${formList[formNumber]}.${elem.label}`;
                                    methods.setValue(fieldName, '', {
                                        shouldValidate: false,
                                        shouldDirty: false,
                                        shouldTouch: false,
                                    });
                                } else if (elem.label === 'Latitude' || elem.label === 'Longitude') {
                                    const fieldName = `${formList[formNumber]}.${elem.label}`;
                                    methods.setValue(fieldName, '', {
                                        shouldValidate: false,
                                        shouldDirty: false,
                                        shouldTouch: false,
                                    });
                                }
                            });
                        }
                    });
                    closeAllSections();
                    if (setDataLoaded && setFilters) {
                        setFilters([]);
                        setDataLoaded(false);
                    }
                    if (onFilterReset) {
                        onFilterReset();
                    }
                }}
            >
                <Trans>Reset</Trans>
            </Button>
        </Box>
    );

    const filterBody = (
        <FormProvider {...methods} {...user}>
            {!collapsible && (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    sx={{ px: 0, pt: 0 }}
                >
                    <Typography
                        sx={{
                            color: theme.palette.mode === "light"
                                ? theme.palette.newColors.gray
                                : theme.palette.newColors.lightGray,
                            fontWeight: 600,
                        }}
                        variant="h5"
                    >
                        <Trans>{"Filters"}</Trans>
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" ml="auto">
                        {filterActions}
                    </Box>
                </Box>
            )}

            <Divider flexItem sx={{ mt: collapsible ? 0 : -1, mx: collapsible ? 2 : 0 }} />

            <List
                sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                    borderRadius: collapsible ? 0 : 2,
                    overflow: "hidden",
                    boxShadow: collapsible ? 0 : 1,
                    mx: collapsible ? 2 : 0,
                    mb: collapsible ? 2 : 0,
                }}
            >
                {formList.map((item, formNumber) => (
                    <Box key={item}>
                        <ListItemButton
                            onClick={() => handleExpandClick(formNumber)}
                            sx={{
                                color: theme.mode === "light" && theme.palette.newColors.gray,
                                borderBottom:
                                    formNumber !== formList.length - 1 && "1px solid #e0e0e0",
                                px: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                transition: "all 0.2s ease-in-out",
                            }}
                        >
                            <ListItemText
                                primary={(() => {
                                    const displayName = formModel[item].displayName
                                    const known = {
                                        'crash': _(msg`Crash`),
                                        'people': _(msg`Person`),
                                        'units': _(msg`Traffic unit`),
                                        'road': _(msg`Road`),
                                        'Crash': _(msg`Crash`),
                                        'Road': _(msg`Road`),
                                        'Traffic unit': _(msg`Traffic unit`),
                                        'Person': _(msg`Person`),
                                    }
                                    return known[displayName] ?? displayName
                                })()}
                                primaryTypographyProps={{
                                    fontWeight: expanded[formNumber] ? 600 : 500,
                                    fontSize: 15,
                                }}
                            />
                            {expanded[formNumber] ? (
                                <ExpandLess
                                    sx={{ color: theme.mode === "light" && theme.palette.newColors.gray }}
                                />
                            ) : (
                                <ExpandMoreIcon
                                    sx={{ color: theme.mode === "light" && theme.palette.newColors.gray }}
                                />
                            )}
                        </ListItemButton>

                        <Collapse sx={{boxShadow:'-2px 0px 1px rgba(0, 0, 0, 0.2)'}} in={expanded[formNumber]} timeout="auto" unmountOnExit={false}>
                            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: theme.mode === "light" && "#fafafa" }}>
                                <StatsFilterForm
                                    title={title}
                                    isMobile={isMobile}
                                    formModel={formModel}
                                    submitRef={submitRefs.current[formNumber]}
                                    formList={formList}
                                    form={{
                                        number: formNumber,
                                        type: formList[formNumber],
                                        displayName: formModel[formList[formNumber]].displayName,
                                    }}
                                    handleFilterObjSubmit={handleFilterSubmit}
                                />
                            </Box>
                        </Collapse>
                    </Box>
                ))}
            </List>
        </FormProvider>
    );

    if (collapsible) {
        return (
            <CollapsiblePanelCard
                title={<Trans>Filters</Trans>}
                defaultExpanded={defaultExpanded}
                expandAriaLabel={_(msg`Toggle filters panel`)}
                headerAction={filterActions}
                sx={{ mt: 1 }}
            >
                {filterBody}
            </CollapsiblePanelCard>
        );
    }

    return (
        <Paper
            square
            sx={{
                display: "flex",
                flexDirection: "column",
                mt: 1,
                gap: 2,
                pt: 3,
                pb: 3,
                pl: 2,
                pr: 2,
                minHeight: 350,
            }}
        >
            {filterBody}
        </Paper>
    );
};

export default StatisticsFilterPanel;
